export type Shape =
  | "triangle"
  | "square"
  | "pentagon"
  | "hexagon"
  | "octagon";

export type PickupType = "score" | "shield" | "slowmo" | "magnet" | "double";

export interface PickupPlacement {
  type: PickupType;
  // Panel index 0..(sides-1). For score orbs use a non-gap panel; for
  // shield / slow-mo orbs use a gap panel for risk-reward.
  panel: number;
  // Position along the segment in [0, 1] where 0 = back (far from
  // player at spawn) and 1 = front (closer to player at spawn). Avoid
  // values < ~0.3 for risk pickups so the player has time to react.
  z: number;
}

// Optional gameplay twist applied to a single shape segment. Each
// trick has a dedicated FX system (PulsePanelsFx / MirrorTrickFx /
// SweepFx) and a slightly tweaked SegmentFactory build path so the
// rest of the level pipeline doesn't have to special-case anything.
//   pulse  -- panel emissive dies on every kick, players have to read
//             the gap layout in advance because it goes black on beat.
//   mirror -- left/right rotate inputs are swapped while the player
//             is inside this segment.
//   sweep  -- a glowing kill-bar moves across panels in sync with the
//             snare; the player has to NOT be on the panel the bar is
//             about to land on.
export type SegmentTrick = "pulse" | "mirror" | "sweep";

// Marks a segment as the START of a boss phrase. The streamer fires
// onBossStart with this info on attach and onBossCleared when the
// final segment of the phrase recycles. `bossEnd: true` flags the
// last segment of the phrase so the streamer knows when to fire the
// "cleared" event.
export interface BossMarker {
  id: string;
  banner: string;
}

export interface ShapeSegment {
  shape: Shape;
  speed: number;
  length: number;
  gaps: number[];
  pickups?: PickupPlacement[];
  trick?: SegmentTrick;
  boss?: BossMarker;
  bossEnd?: boolean;
  // Optional sustained camera roll while the player is inside this
  // segment (-1 banks left, 1 banks right). BankingFx reads this each
  // frame and tweens CameraBank.setSustainedRoll. Hand-authored levels
  // can use this to telegraph "lean into the next gate"; the endless
  // generator sprinkles it after a warmup.
  bank?: -1 | 1;
}

export interface GateSegment {
  shape: "gate";
  to: Shape;
}

export type LevelEntry = ShapeSegment | GateSegment;

export function isGate(entry: LevelEntry): entry is GateSegment {
  return entry.shape === "gate";
}
